/**
 * AI Entity Manager
 * 
 * Centralized management of AI entities.
 * Handles creation, retrieval, updates, and ownership.
 */

import { db } from "@/lib/db";
import type { AIEntity } from "@/types/aiEntity";
import { aiProfileToEntity, entityToAiProfile } from "@/types/aiEntity";
import { resolveOwnerUserId } from "@/lib/user/identity";
import type { AiProfile } from "@/types/ai";

/**
 * Get all AIs owned by the current user
 */
export async function getUserAIs(userId?: string): Promise<AIEntity[]> {
  const ownerId = userId ?? (await resolveOwnerUserId());
  
  try {
    // Query by ownerId
    const profiles = await db.aiProfiles
      .where("ownerId")
      .equals(ownerId)
      .sortBy("updatedAt");
    
    // Convert to entities and reverse (newest first)
    return profiles.reverse().map(aiProfileToEntity);
  } catch (error) {
    console.error("Failed to get user AIs:", error);
    return [];
  }
}

/**
 * Get AI entity by ID (with ownership check)
 */
export async function getAIEntity(id: string, userId?: string): Promise<AIEntity | null> {
  const ownerId = userId ?? (await resolveOwnerUserId());
  
  try {
    const profile = await db.aiProfiles.get(id);
    if (!profile) return null;
    
    // Ownership check
    if (profile.ownerId !== ownerId) {
      console.warn(`Access denied: AI ${id} owned by ${profile.ownerId}, requested by ${ownerId}`);
      return null;
    }
    
    return aiProfileToEntity(profile);
  } catch (error) {
    console.error("Failed to get AI entity:", error);
    return null;
  }
}

/**
 * Create a new AI entity
 */
export async function createAIEntity(data: Partial<AIEntity>): Promise<AIEntity> {
  const userId = await resolveOwnerUserId();
  const now = Date.now();
  
  const entity: AIEntity = {
    id: data.id || crypto.randomUUID(),
    name: data.name || "My AI",
    description: data.description,
    ownerId: userId, // Always use real userId
    createdAt: data.createdAt || now,
    updatedAt: now,
    modePreset: data.modePreset || "playful",
    brand: data.brand || {
      primaryColor: "#6366f1",
      secondaryColor: "#8b5cf6",
      textColor: "#000000",
      textSecondaryColor: "rgba(0, 0, 0, 0.7)",
      iconDataUrl: null,
    },
    tiles: data.tiles || [],
    sliders: data.sliders || {
      formality: 50,
      verbosity: 50,
      boldness: 50,
      proactivity: 50,
    },
    privacyMode: data.privacyMode || "smart",
    agentModeEnabled: data.agentModeEnabled || false,
    version: data.version || 1,
    parentEntityId: data.parentEntityId,
    templateId: data.templateId,
    isPublic: data.isPublic || false,
    shareSlug: data.shareSlug,
    customDomain: data.customDomain,
    publishedAt: data.publishedAt,
    viewCount: 0,
    cloneCount: 0,
    teamId: data.teamId,
    customCSS: data.customCSS,
    customTheme: data.customTheme,
    customInstructions: data.customInstructions,
    plan: data.plan || "free",
    lastActivityAt: now,
    conversationCount: 0,
    messageCount: 0,
  };
  
  // Save as AiProfile for backward compatibility
  const profile = entityToAiProfile(entity);
  await db.aiProfiles.add(profile as AiProfile);
  
  return entity;
}

/**
 * Update AI entity
 */
export async function updateAIEntity(id: string, updates: Partial<AIEntity>, userId?: string): Promise<AIEntity | null> {
  const ownerId = userId ?? (await resolveOwnerUserId());
  
  // Verify ownership
  const existing = await db.aiProfiles.get(id);
  if (!existing || existing.ownerId !== ownerId) {
    throw new Error("Access denied or AI not found");
  }
  
  // Convert to entity, merge updates, convert back
  const entity = aiProfileToEntity(existing);
  const updated = { ...entity, ...updates, updatedAt: Date.now() };
  const profile = entityToAiProfile(updated);
  
  await db.aiProfiles.update(id, profile as AiProfile);
  
  return updated;
}

/**
 * Delete AI entity
 */
export async function deleteAIEntity(id: string, userId?: string): Promise<boolean> {
  const ownerId = userId ?? (await resolveOwnerUserId());
  
  const existing = await db.aiProfiles.get(id);
  if (!existing || existing.ownerId !== ownerId) {
    return false;
  }
  
  await db.aiProfiles.delete(id);
  return true;
}

/**
 * Get active AI (most recently updated)
 */
export async function getActiveAI(userId?: string): Promise<AIEntity | null> {
  const ais = await getUserAIs(userId);
  return ais.length > 0 ? ais[0] : null;
}

/**
 * Set active AI (update lastActivityAt)
 */
export async function setActiveAI(id: string, userId?: string): Promise<void> {
  await updateAIEntity(id, { lastActivityAt: Date.now() }, userId);
}

